/**
 * DESIGN.md § Typography: "Dates render as 'March 31, 2027' in prose and
 * headings and 'Mar 31, 2027' in table cells; never '03/31/27'." This is
 * the one place that cell-format rule lives — a column's `render` calls
 * this rather than each column config re-implementing `Intl.DateTimeFormat`
 * with its own options (and risking a slightly different format per list).
 */
const CELL_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "America/New_York",
});

/**
 * Found live while verifying Story 11.5's own new `serve_by` field (a real
 * `DATE`-only column, AD-6): a bare `YYYY-MM-DD` string with no time
 * component — every calendar-date field this app has (lease end dates,
 * document expirations, tenant dates of birth, notice serve-by dates...)
 * serializes exactly this way — parses via `new Date(...)` as UTC midnight,
 * and formatting that instant in `America/New_York` (this formatter's own
 * zone, correct for a real timestamp) walks it back to the PREVIOUS
 * calendar day for roughly half of every 24-hour period. Confirmed live and
 * reproducibly: `new Date("2027-01-02")` formatted in America/New_York in
 * January renders "Jan 1, 2027" — silently wrong by one day, for a class of
 * field this app uses for real legal/financial deadlines. A genuine
 * timestamp (`"2026-08-17T12:00:00Z"`, this function's own original
 * example) has no such problem — noon UTC never crosses a US Eastern day
 * boundary either direction, which is exactly why nothing here noticed this
 * until a bare date-only value went through the same path for the first
 * time. This formatter is UTC-zoned specifically so a date-only string's
 * own UTC-midnight parse renders on the SAME calendar day it names, with no
 * possible timezone-dependent shift — the correct behavior for a calendar
 * date, which (unlike an instant) has no timezone of its own to begin with.
 */
const CELL_DATE_ONLY_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** `"2026-08-17T12:00:00Z"` -> `"Aug 17, 2026"`; `"2027-01-02"` (a bare
 * calendar date, no time component) -> `"Jan 2, 2027"` — never shifted to
 * the previous day by the viewer's own local timezone. */
export function formatDateCell(isoDateTime: string): string {
  const formatter = DATE_ONLY_PATTERN.test(isoDateTime) ? CELL_DATE_ONLY_FORMATTER : CELL_DATE_FORMATTER;
  return formatter.format(new Date(isoDateTime));
}

/**
 * Story 6.7's own money-column formatter — the one place a `_cents`
 * integer becomes a displayed dollar string, mirroring `formatDateCell`'s
 * identical "one shared formatter, not one per list" precedent. Every
 * money column also sets `tabularFigures` and `align: "right"` on its own
 * `DataTableColumn` (DESIGN.md § Typography/§ Components) — this function
 * only produces the text, never the alignment.
 */
const CELL_MONEY_FORMATTER = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

/** `180000` (cents) -> `"$1,800.00"`; `null` -> `"—"`. */
export function formatMoneyCell(cents: number | null): string {
  if (cents === null) {
    return "—";
  }
  return CELL_MONEY_FORMATTER.format(cents / 100);
}

/**
 * Story 13.2's own "Portfolio at a glance" occupancy % (R-H4) — the API
 * layer rounds to one decimal place already (`app/domain/dashboard/
 * portfolio_summary.py`), so this formatter's only job is dropping a
 * trailing `.0` a whole-number percentage would otherwise carry (`50.0` ->
 * `"50%"`, `33.3` -> `"33.3%"`), mirroring `formatMoneyCell`'s identical
 * "one shared formatter, not a hand-rolled `toFixed()` per call site"
 * precedent.
 */
const PERCENT_FORMATTER = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });

/** `50.0` -> `"50%"`; `33.3` -> `"33.3%"`; `0` -> `"0%"`. */
export function formatPercentCell(value: number): string {
  return `${PERCENT_FORMATTER.format(value)}%`;
}

/**
 * Story 8.5's own date-picker default: today's local date, in
 * `<input type="date">`'s own `YYYY-MM-DD` format — a *suggestion* only.
 * Shared by `ImportWizard.tsx`'s own generate-from step and `ImportBatches.
 * tsx`'s own "set generate-from date" dialog, rather than each computing it
 * independently. `POST /import-batches/{id}/generate-from`'s own docstring
 * has the full reasoning for why the picker built from this default carries
 * no `min`/`max` — any real calendar date, past or future, stays selectable.
 */
export function todayAsDateInputValue(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Story 4.5's own `BackupRun`/`RestoreTestRun` `outcome` column ("success"/
 * "failure") rendered for a person ("Success"/"Failure") — originally a
 * small, local helper in `BackupStatus.tsx` (that screen's own comment:
 * "No per-screen label map addition for this... a plain, local transform"),
 * promoted here once Story 13.3's own "Data health" dashboard panel became
 * a second real caller reading the exact same `outcome` vocabulary — the
 * same "shared once two real consumers exist" discipline `formatDateCell`/
 * `formatMoneyCell` above already establish, so the two screens can never
 * silently drift into two different renderings of the same raw string.
 */
export function formatOutcome(outcome: string): string {
  if (outcome.length === 0) {
    return outcome;
  }
  return outcome.charAt(0).toUpperCase() + outcome.slice(1);
}

/**
 * `1234567` -> `"1.18 MB"`; `null` -> `"—"`. Promoted from `BackupStatus.
 * tsx`'s own local helper for the identical reason `formatOutcome` above
 * was — Story 13.3's own "Data health" dashboard panel is a second real
 * caller over the exact same `storage_used_bytes` figure (both ultimately
 * `app.repo.documents.total_document_bytes()`, `app/api/dashboard.py`'s own
 * module docstring), and a shared formatter is what keeps the two screens'
 * own renderings of that one real number from ever silently disagreeing.
 */
export function formatBytes(bytes: number | null): string {
  if (bytes === null) {
    return "—";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
}
